/**

 * Cloud progress sync — same Firebase uid on web / desktop / Android.

 * localStorage remains an offline cache; Firestore is source of truth when signed in.

 */

import { doc, onSnapshot, serverTimestamp, setDoc } from "firebase/firestore";

import {
  DEFAULT_PROGRESS,
  MAX_CURRICULUM_STAGE,
  type ProgressState,
} from "@piano-tutor/shared";

import { getDb, getFirebaseAuth } from "./config";



const LOCAL_KEY = "pianoTutorProgress";



export function readLocalProgress(): ProgressState {

  try {

    const raw = localStorage.getItem(LOCAL_KEY);

    if (!raw) return { ...DEFAULT_PROGRESS, completedClassIds: [], passedExamIds: [] };

    return normalizeProgress(JSON.parse(raw) as ProgressState);

  } catch {

    return { ...DEFAULT_PROGRESS, completedClassIds: [], passedExamIds: [] };

  }

}



export function writeLocalProgress(progress: ProgressState): void {

  try {

    localStorage.setItem(LOCAL_KEY, JSON.stringify(normalizeProgress(progress)));

  } catch {

    /* ignore */

  }

}



function normalizeProgress(p: Partial<ProgressState> | null | undefined): ProgressState {

  const unlocked = Math.max(
    1,
    Math.min(MAX_CURRICULUM_STAGE, Number(p?.unlockedStage) || 1),
  );

  const completedClassIds = Array.isArray(p?.completedClassIds)

    ? [...new Set(p!.completedClassIds.filter((x) => typeof x === "string"))]

    : [];

  const passedExamIds = Array.isArray(p?.passedExamIds)

    ? [...new Set(p!.passedExamIds.filter((x) => typeof x === "string"))]

    : [];

  return { unlockedStage: unlocked, completedClassIds, passedExamIds };

}



/** Merge: highest stage + union of class/exam IDs (never lose progress). */

export function mergeProgress(a: ProgressState, b: ProgressState): ProgressState {

  const na = normalizeProgress(a);

  const nb = normalizeProgress(b);

  return {

    unlockedStage: Math.max(na.unlockedStage, nb.unlockedStage),

    completedClassIds: [...new Set([...na.completedClassIds, ...nb.completedClassIds])],

    passedExamIds: [...new Set([...na.passedExamIds, ...nb.passedExamIds])],

  };

}



function progressEqual(a: ProgressState, b: ProgressState): boolean {

  const na = normalizeProgress(a);

  const nb = normalizeProgress(b);

  if (na.unlockedStage !== nb.unlockedStage) return false;

  if (na.completedClassIds.length !== nb.completedClassIds.length) return false;

  if (na.passedExamIds.length !== nb.passedExamIds.length) return false;

  const c = new Set(na.completedClassIds);

  for (const id of nb.completedClassIds) if (!c.has(id)) return false;

  const e = new Set(na.passedExamIds);

  for (const id of nb.passedExamIds) if (!e.has(id)) return false;

  return true;

}



type ApplyFn = (progress: ProgressState) => void;



let unsub: (() => void) | null = null;

let applyRemote: ApplyFn | null = null;

let lastPushed = "";

let pushTimer: ReturnType<typeof setTimeout> | null = null;

let syncingFromCloud = false;



export function bindProgressCloudSync(apply: ApplyFn): () => void {

  applyRemote = apply;

  const auth = getFirebaseAuth();

  if (!auth) return () => undefined;



  const startWatch = (uid: string) => {

    unsub?.();

    const db = getDb();

    if (!db) return;

    unsub = onSnapshot(doc(db, "users", uid), (snap) => {

      if (!snap.exists()) return;

      const data = snap.data() as { progress?: ProgressState };

      if (!data.progress) return;

      const remote = normalizeProgress(data.progress);

      const local = readLocalProgress();

      const merged = mergeProgress(local, remote);

      writeLocalProgress(merged);

      if (!progressEqual(merged, remote)) {

        void persistProgressToCloud(merged, true);

      }

      syncingFromCloud = true;

      try {

        applyRemote?.(merged);

      } finally {

        syncingFromCloud = false;

      }

    });

  };



  const stopAuth = auth.onAuthStateChanged((user) => {

    unsub?.();

    unsub = null;

    if (!user) return;

    const local = readLocalProgress();

    void persistProgressToCloud(local, true).then(() => startWatch(user.uid));

  });



  return () => {

    stopAuth();

    unsub?.();

    unsub = null;

    if (pushTimer) clearTimeout(pushTimer);

  };

}



export async function persistProgressToCloud(

  progress: ProgressState,

  immediate = false,

): Promise<void> {

  if (syncingFromCloud && !immediate) return;

  const auth = getFirebaseAuth();

  const user = auth?.currentUser;

  const db = getDb();

  if (!user || !db) return;



  const normalized = normalizeProgress(progress);

  writeLocalProgress(normalized);

  const payload = JSON.stringify(normalized);

  if (payload === lastPushed && !immediate) return;



  const run = async () => {

    lastPushed = payload;

    await setDoc(

      doc(db, "users", user.uid),

      {

        progress: normalized,

        updatedAt: serverTimestamp(),

      },

      { merge: true },

    );

  };



  if (immediate) {

    try {

      await run();

    } catch {

      /* offline / rules */

    }

    return;

  }



  if (pushTimer) clearTimeout(pushTimer);

  pushTimer = setTimeout(() => {

    void run().catch(() => undefined);

  }, 600);

}



export function saveProgressEverywhere(progress: ProgressState): void {

  writeLocalProgress(progress);

  void persistProgressToCloud(progress);

}


